// The vendor's payout contract in the Company section (Company → Contract):
// the contract waiting to be signed and the history, the same ContractCard the
// admin's vendor page shows. Its data is the payouts overview's `contract`,
// reloaded at once when a contract is sent, signed or withdrawn anywhere
// (aldoo-com-api vendor-submissions broadcast "submissions:contract").
import React, { useCallback, useEffect, useState } from "react"
import { Empty, LoadError } from "../AdminShared/ui"
import { PayoutsOverview } from "../AdminShared/api"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { PAYOUTS } from "../Vendor/texts/bundles"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"
import useSubmissionEvents from "../Vendor/useSubmissionEvents"
import ContractCard from "./ContractCard"

/** The org's contract (the overview's `contract`): { contract, loading, reload }. */
export function useContract(orgId, { enabled = true } = {}) {
  const [data, setData] = useState(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((n) => n + 1), [])
  useSubmissionEvents({ orgId: orgId || null, enabled: enabled && !!orgId, events: ["submissions:contract"], onEvent: reload })
  useEffect(() => {
    if (!enabled || !orgId) return
    let alive = true
    PayoutsOverview(orgId)
      .then((res) => alive && setData(res || {}))
      .catch(() => alive && setData({ error: true }))
    return () => {
      alive = false
    }
  }, [orgId, tick, enabled])
  return { contract: data?.contract, loading: !data, error: !!data?.error, reload }
}

export default function ContractTab({ orgId, state, canSign, admin }) {
  const t = usePortalTexts(PAYOUTS)
  const locale = useSiteLanguage() === "bg" ? "bg-BG" : "en-GB"
  if (state.loading) return <Empty>{t`Loading…`}</Empty>
  if (state.error) return <LoadError title={t`Could not load the payouts`} onRetry={state.reload} testId="contract-load-error" />
  return <ContractCard orgId={orgId} contract={state.contract} canSign={canSign} admin={admin} onChanged={state.reload} locale={locale} />
}
